/**
 * VieNeu-TTS v3 Turbo — browser engine (Apache-2.0).
 * Heavy ONNX work runs in the TTS worker; this file is the TtsEngine facade.
 */

import {
  CloneResult,
  LoadProgress,
  PresetVoice,
  SynthOptions,
  SynthResult,
  TtsEngine,
} from './types';
import { callTtsWorker, isWorkerAvailable, terminateTtsWorker } from './workerClient';

const MODEL_ID = 'vieneu-v3-turbo';
const DISK_FLAG = 'l123-tts-disk:' + MODEL_ID;

let ready = false;
let abortFlag = false;
let presetCache: PresetVoice[] | null = null;
let diskCached: boolean | null = null;

function readDiskFlag(): boolean {
  try {
    const g: any = typeof global !== 'undefined' ? global : {};
    const ls = (g.window || g).localStorage;
    return !!(ls && ls.getItem(DISK_FLAG) === '1');
  } catch {
    return false;
  }
}

function writeDiskFlag(on: boolean) {
  try {
    const g: any = typeof global !== 'undefined' ? global : {};
    const ls = (g.window || g).localStorage;
    if (!ls) return;
    if (on) ls.setItem(DISK_FLAG, '1');
    else ls.removeItem(DISK_FLAG);
  } catch {
    /* ignore */
  }
}

export const vieneuEngine: TtsEngine = {
  id: 'vieneu',
  supportsCloning: true,
  supportsNativeSpeed: false,
  supportsExaggeration: false,
  canExportFile: true,

  isReady(modelId: string) {
    return modelId === MODEL_ID && ready;
  },

  async hasDiskCache(modelId: string) {
    if (modelId !== MODEL_ID) return false;
    if (diskCached != null) return diskCached;
    if (readDiskFlag()) {
      // Optimistic; confirm via worker when possible
      diskCached = true;
    }
    if (!isWorkerAvailable()) return !!diskCached;
    try {
      const result = await callTtsWorker('cacheStatus', { engine: 'vieneu' });
      diskCached = !!(result && result.diskCached);
      writeDiskFlag(!!diskCached);
      return !!diskCached;
    } catch {
      return !!diskCached;
    }
  },

  async ensureLoaded(modelId: string, onProgress?: (info: LoadProgress) => void) {
    if (modelId !== MODEL_ID) throw new Error('Unknown VieNeu model: ' + modelId);
    if (ready) return;
    if (!isWorkerAvailable()) {
      throw new Error(
        'VieNeu cần Web Worker. Hãy mở /tools/text-to-speech với COOP/COEP (serve:isolated).'
      );
    }
    await callTtsWorker('load', { engine: 'vieneu', modelId }, onProgress);
    ready = true;
    diskCached = true;
    writeDiskFlag(true);
  },

  async listVoices(modelId: string): Promise<PresetVoice[]> {
    if (modelId !== MODEL_ID) return [];
    if (presetCache) return presetCache;
    if (!ready) {
      // Lightweight fetch of preset names without full model
      try {
        const res = await fetch(
          'https://raw.githubusercontent.com/pnnbao97/VieNeu-TTS/main/src/vieneu/assets/voices_v3_turbo.json'
        );
        if (res.ok) {
          const json = await res.json();
          const presets = (json && json.presets) || {};
          presetCache = Object.keys(presets).map((name) => {
            const p = presets[name] || {};
            return {
              id: name,
              label: name + (p.description ? ' — ' + p.description : ''),
              lang: 'vi',
              gender: p.gender,
            };
          });
          return presetCache;
        }
      } catch {
        /* ignore */
      }
      return [];
    }
    const result = await callTtsWorker('listVoices', { engine: 'vieneu' });
    presetCache = (result && result.voices) || [];
    return presetCache || [];
  },

  async cloneVoice(
    modelId: string,
    pcm: Float32Array,
    sampleRate: number,
    onProgress?: (info: LoadProgress) => void
  ): Promise<CloneResult> {
    if (modelId !== MODEL_ID) throw new Error('Unknown model');
    await this.ensureLoaded(modelId, onProgress);
    const result = await callTtsWorker(
      'clone',
      { engine: 'vieneu', pcm, sampleRate },
      onProgress
    );
    return {
      embedding: result.embedding || new Float32Array(0),
      meta: result.meta || {},
      sampleRate: result.sampleRate || 48000,
    };
  },

  async synthesize(modelId: string, opts: SynthOptions): Promise<SynthResult> {
    if (modelId !== MODEL_ID) throw new Error('Unknown model');
    abortFlag = false;
    await this.ensureLoaded(modelId, opts.onProgress);
    if (opts.signal && opts.signal.aborted) throw new Error('Đã hủy.');

    const result = await callTtsWorker(
      'synthesize',
      {
        engine: 'vieneu',
        text: opts.text,
        voiceId: opts.voiceId,
        embedding: opts.embedding,
        referencePcm: opts.referencePcm,
        referenceSampleRate: opts.referenceSampleRate,
        engineMeta: opts.engineMeta,
        lang: opts.lang || 'vi',
      },
      opts.onProgress,
      opts.signal
    );

    if (abortFlag || (opts.signal && opts.signal.aborted)) throw new Error('Đã hủy.');
    return {
      pcm: result.pcm,
      sampleRate: result.sampleRate || 48000,
      nativeSpeedApplied: false,
    };
  },

  cancel() {
    abortFlag = true;
    try {
      callTtsWorker('cancel', {});
    } catch {
      /* ignore */
    }
  },

  async unload(modelId: string) {
    if (modelId !== MODEL_ID) return;
    ready = false;
    presetCache = null;
    // Keep disk cache + localStorage flag — unload only frees RAM
    terminateTtsWorker();
  },
};
